import { useEffect, useMemo, useRef } from 'react';

import { experiments } from '@/utils/experiment/manifest';
import { getExperimentWithExposures } from '@/utils/experiment/use-experiment';
import { useFeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import { useCloudStatus } from './use-cloud-status';
import { useCloudStatusRedirection } from './use-cloud-status-redirection';
import { useCreateSampleProject } from './use-create-sample-project';

/**
 * Hook to handle creating a mature example project based on experiment enrollments.
 */
export const useMatureExampleProjectWithExperiments = (
	isEligibleForMatureExampleProjectCreation: boolean,
) => {
	const { addBlockingRedirectPromise } = useCloudStatusRedirection();
	const createSampleProjectRequestInitialized = useRef(false);
	const { createSampleProject } = useCreateSampleProject();
	const { createCloudStatus } = useCloudStatus();

	const isMatureSampleProjectV1Variation = useMemo(() => {
		if (!isEligibleForMatureExampleProjectCreation) {
			return false;
		}

		const sampleProjectExperiment = getExperimentWithExposures(
			experiments.jfindMatureExampleProjectExperiment,
		);

		return sampleProjectExperiment.type === 'enrolled' && sampleProjectExperiment.value;
	}, [isEligibleForMatureExampleProjectCreation]);

	useFeatureExposed('jfind-mature-example-project', 'Test', {
		conditions: [
			isEligibleForMatureExampleProjectCreation &&
				createCloudStatus === 'success' &&
				isMatureSampleProjectV1Variation,
		],
	});

	useEffect(() => {
		if (
			isMatureSampleProjectV1Variation &&
			createCloudStatus === 'success' &&
			createSampleProjectRequestInitialized.current === false
		) {
			createSampleProjectRequestInitialized.current = true;
			const matureExampleProjectV1Promise = createSampleProject();
			addBlockingRedirectPromise(matureExampleProjectV1Promise);
		}
	}, [
		isMatureSampleProjectV1Variation,
		createCloudStatus,
		addBlockingRedirectPromise,
		createSampleProject,
	]);
};
